import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getMessages, updateMessage, deleteMessage } from '../utils/storage';
import { format } from 'date-fns';
import supabase from '../lib/supabase';

const { FiShield, FiCheck, FiX, FiFlag, FiTrash2, FiLogOut, FiEye, FiUsers, FiMessageSquare, FiAlertTriangle, FiLoader } = FiIcons;

const AdminDashboard = () => {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check auth status
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        navigate('/admin');
        return;
      }
      setUser(data.session.user);
      loadMessages();
    };

    checkAuth();
  }, [navigate]);

  const loadMessages = async () => {
    try {
      const allMessages = await getMessages();
      setMessages(allMessages);
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (messageId) => {
    await updateMessage(messageId, { approved: true, flagged: false });
    loadMessages();
  };

  const handleFlag = async (messageId) => {
    await updateMessage(messageId, { flagged: true, approved: false });
    loadMessages();
  };

  const handleDelete = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      await deleteMessage(messageId);
      loadMessages();
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const filteredMessages = messages.filter(message => {
    switch (filter) {
      case 'pending':
        return !message.approved && !message.flagged;
      case 'approved':
        return message.approved;
      case 'flagged':
        return message.flagged;
      default:
        return true;
    }
  });

  const stats = {
    total: messages.length,
    approved: messages.filter(m => m.approved).length,
    flagged: messages.filter(m => m.flagged).length,
    pending: messages.filter(m => !m.approved && !m.flagged).length
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="clay-card bg-white p-8 text-center">
          <div className="flex items-center justify-center space-x-3">
            <SafeIcon icon={FiLoader} className="text-3xl text-text animate-spin" />
            <h2 className="text-2xl font-bold text-heading">Loading dashboard...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="flex flex-wrap items-center justify-between gap-4 mb-8"
      >
        <div className="flex items-center space-x-4">
          <div className="clay-button p-3">
            <SafeIcon icon={FiShield} className="text-2xl text-text" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-text">Admin Dashboard</h1>
            <p className="text-text">Content moderation and platform management</p>
            {user && (
              <p className="text-sm text-text/80 mt-1">
                Logged in as: {user.email}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="clay-button px-4 py-2 flex items-center space-x-2 text-text hover:shadow-clay-hover transition-all duration-300 focus-visible"
        >
          <SafeIcon icon={FiLogOut} className="text-lg text-text" />
          <span>Logout</span>
        </button>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        <div className="clay-card bg-white p-6 text-center">
          <div className="clay-button p-3 mx-auto mb-3 w-fit">
            <SafeIcon icon={FiMessageSquare} className="text-xl text-text" />
          </div>
          <div className="text-2xl font-bold text-text">{stats.total}</div>
          <div className="text-sm text-text">Total Messages</div>
        </div>
        <div className="clay-card bg-white p-6 text-center">
          <div className="clay-button p-3 mx-auto mb-3 w-fit">
            <SafeIcon icon={FiCheck} className="text-xl text-text" />
          </div>
          <div className="text-2xl font-bold text-text">{stats.approved}</div>
          <div className="text-sm text-text">Approved</div>
        </div>
        <div className="clay-card bg-white p-6 text-center">
          <div className="clay-button p-3 mx-auto mb-3 w-fit">
            <SafeIcon icon={FiAlertTriangle} className="text-xl text-text" />
          </div>
          <div className="text-2xl font-bold text-text">{stats.pending}</div>
          <div className="text-sm text-text">Pending Review</div>
        </div>
        <div className="clay-card bg-white p-6 text-center">
          <div className="clay-button p-3 mx-auto mb-3 w-fit">
            <SafeIcon icon={FiFlag} className="text-xl text-text" />
          </div>
          <div className="text-2xl font-bold text-text">{stats.flagged}</div>
          <div className="text-sm text-text">Flagged</div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="clay-card bg-white p-6 mb-8 overflow-x-auto"
      >
        <div className="flex flex-wrap gap-3">
          {[
            { key: 'all', label: 'All Messages', count: stats.total },
            { key: 'pending', label: 'Pending Review', count: stats.pending },
            { key: 'approved', label: 'Approved', count: stats.approved },
            { key: 'flagged', label: 'Flagged', count: stats.flagged }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-2xl transition-all duration-300 focus-visible ${
                filter === key
                  ? 'clay-button shadow-clay-hover text-text font-medium'
                  : 'bg-primary/10 hover:bg-primary/20 text-text'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </motion.div>

      {/* Messages List */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="space-y-4"
      >
        {filteredMessages.length === 0 ? (
          <div className="clay-card bg-white p-8 text-center">
            <div className="clay-button p-4 mx-auto mb-4 w-fit">
              <SafeIcon icon={FiMessageSquare} className="text-2xl text-text/40" />
            </div>
            <h3 className="text-lg font-semibold text-text mb-2">No Messages Found</h3>
            <p className="text-text">No messages match the current filter.</p>
          </div>
        ) : (
          filteredMessages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.05 * index, duration: 0.3 }}
              className={`clay-card bg-white p-6 ${
                message.flagged
                  ? 'border-l-4 border-red-400'
                  : message.approved
                    ? 'border-l-4 border-green-400'
                    : 'border-l-4 border-yellow-400'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div
                      className={`clay-button p-2 ${
                        message.flagged
                          ? 'bg-red-50'
                          : message.approved
                            ? 'bg-green-50'
                            : 'bg-yellow-50'
                      }`}
                    >
                      <SafeIcon
                        icon={message.flagged ? FiFlag : message.approved ? FiCheck : FiAlertTriangle}
                        className={`text-lg ${
                          message.flagged
                            ? 'text-red-500'
                            : message.approved
                              ? 'text-green-500'
                              : 'text-yellow-500'
                        }`}
                      />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm text-text">ID: {message.id}</span>
                        <span className="text-sm text-text hidden sm:inline">•</span>
                        <span className="text-sm text-text">
                          {format(new Date(message.timestamp), 'MMM d, yyyy HH:mm')}
                        </span>
                      </div>
                      <div className="text-sm text-text">
                        From: {message.isAnonymous ? 'Anonymous' : message.sender}
                      </div>
                    </div>
                  </div>

                  {message.title && (
                    <h3 className="text-lg font-semibold text-heading mb-2">{message.title}</h3>
                  )}

                  <blockquote className="text-text text-lg leading-relaxed mb-4">
                    "{message.content}"
                  </blockquote>

                  <div className="flex flex-wrap items-center gap-3">
                    {!message.approved && !message.flagged && (
                      <button
                        onClick={() => handleApprove(message.id)}
                        className="clay-button px-4 py-2 flex items-center space-x-2 text-text hover:shadow-clay-hover transition-all duration-300 focus-visible"
                      >
                        <SafeIcon icon={FiCheck} className="text-sm text-green-600" />
                        <span className="text-sm">Approve</span>
                      </button>
                    )}

                    {!message.flagged && (
                      <button
                        onClick={() => handleFlag(message.id)}
                        className="clay-button px-4 py-2 flex items-center space-x-2 text-text hover:shadow-clay-hover transition-all duration-300 focus-visible"
                      >
                        <SafeIcon icon={FiFlag} className="text-sm text-yellow-600" />
                        <span className="text-sm">Flag</span>
                      </button>
                    )}

                    <button
                      onClick={() => window.open(`/message/${message.id}`, '_blank')}
                      className="clay-button px-4 py-2 flex items-center space-x-2 text-text hover:shadow-clay-hover transition-all duration-300 focus-visible"
                    >
                      <SafeIcon icon={FiEye} className="text-sm text-text" />
                      <span className="text-sm">View</span>
                    </button>

                    <button
                      onClick={() => handleDelete(message.id)}
                      className="clay-button px-4 py-2 flex items-center space-x-2 text-text hover:shadow-clay-hover transition-all duration-300 focus-visible"
                    >
                      <SafeIcon icon={FiTrash2} className="text-sm text-red-600" />
                      <span className="text-sm">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </motion.div>
    </div>
  );
};

export default AdminDashboard;